import React, { useEffect, useState } from 'react';
import { FaGoogle, FaRegCalendarCheck, FaSlack } from 'react-icons/fa';
import { Button, Card, Spinner, Row, Col, Form } from 'react-bootstrap';
import Cookies from 'js-cookie';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import WorkspaceCards from '../../components/workspace-card/WorkspaceCard';

export default function AuthCalendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(!!Cookies.get('google_access_token'));
  const [isChecking, setIsChecking] = useState(true);
  const [workspaceInput, setWorkspaceInput] = useState('');
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const error = params.get('error');

    if (accessToken) {
      Cookies.set('google_access_token', accessToken, {
        secure: true,
        expires: 365,
      });

      if (refreshToken) {
        Cookies.set('google_refresh_token', refreshToken, {
          secure: true,
        });
      }

      setIsAuthenticated(true);
      navigate('/slack/dashboard/calendar', { replace: true });
    }

    if (error) {
      console.error('Google Auth Error:', error);
      setIsAuthenticated(false);
    }

    setIsChecking(false);
  }, [location, navigate]);

  const handleGoogleAuth = () => {
    setIsGoogleAuthLoading(true);
    try {
      window.location.href = `${process.env.REACT_APP_API_URL}/google-auth`;
    } catch (err) {
      console.error('Error initiating Google Auth:', err.message);
      setIsGoogleAuthLoading(false);
    }
  };

  const handleSearchWorkspace = async () => {
    setIsWorkspaceLoading(true);
    try {
      const token = Cookies.get("token");
      if (!token) {
        throw new Error("Please log in to connect a Slack workspace");
      }
      if (!workspaceInput.trim()) {
        throw new Error("Please enter a workspace name");
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/slack`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            workspace: workspaceInput,
          },
        }
      );

      const data = res.data;
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No redirect URL received");
      }
    } catch (err) {
      console.error("Error connecting to Slack:", err.message);
      setIsWorkspaceLoading(false);
    }
  };

  if (isChecking) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container">
      <Row className="justify-content-center gx-5">
        <Col md="auto" className="mb-4">
          <Card
            className="text-center px-4 py-4 shadow-sm"
            style={{ width: '600px', borderRadius: '8px' }}
          >
            <Card.Body className="d-flex flex-column justify-content-between h-100">
              <div>
                <FaSlack size={40} className="text-dark mb-3" />
                <Card.Title className="mb-3 fs-5">Connect to Slack Workspace</Card.Title>
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="Enter workspace name"
                    value={workspaceInput}
                    onChange={(e) => setWorkspaceInput(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div>
                <Button
                  variant="primary"
                  className="d-flex align-items-center justify-content-center gap-2 w-100 py-2 mt-3"
                  style={{ minHeight: '40px' }}
                  onClick={handleSearchWorkspace}
                  disabled={isWorkspaceLoading}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '150px',
                      height: '24px',
                    }}
                  >
                    {isWorkspaceLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        style={{ width: '20px', height: '20px' }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      'Connect Other Workspace'
                    )}
                  </span>
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md="auto" className="mb-4">
          {isAuthenticated ? (
            <Card
              className="text-center px-4 py-4 shadow-sm"
              style={{ width: '600px', borderRadius: '8px' }}
            >
              <Card.Body className="d-flex flex-column justify-content-between h-100">
                <div>
                  <FaRegCalendarCheck size={40} className="text-success mb-3" />
                  <Card.Title className="mb-3 fs-5">Connected to Google Calendar</Card.Title>
                  <Card.Text className="text-muted">
                    Your account is successfully authenticated with Google Calendar.
                  </Card.Text>
                </div>
                <Button
                  variant="primary"
                  className="d-flex align-items-center justify-content-center gap-2 w-100 py-2 mt-3"
                  style={{ minHeight: '40px' }}
                  onClick={() => (window.location.href = 'https://calendar.google.com/calendar/u/0/r')}
                  disabled={isGoogleAuthLoading}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '150px',
                      height: '24px',
                    }}
                  >
                    {isGoogleAuthLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        style={{ width: '20px', height: '20px' }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      <>
                        <FaGoogle />  Go to Calendar Dashboard
                      </>
                    )}
                  </span>
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Card
              className="text-center px-4 py-4 shadow-sm"
              style={{ width: '600px', borderRadius: '8px' }}
            >
              <Card.Body className="d-flex flex-column justify-content-between h-100">
                <div>
                  <FaGoogle size={40} className="text-danger mb-3" />
                  <Card.Title className="mb-3 mt-3 fs-5">Connect to Google Calendar</Card.Title>
                  <Card.Text className="text-muted">
                    Authenticate to sync tasks with your Google Calendar.
                  </Card.Text>
                </div>
                <Button
                  variant="danger"
                  className="d-flex align-items-center justify-content-center gap-2 w-100 py-2 mt-3"
                  style={{ minHeight: '40px' }}
                  onClick={handleGoogleAuth}
                  disabled={isGoogleAuthLoading}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '150px',
                      height: '24px',
                    }}
                  >
                    {isGoogleAuthLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        style={{ width: '20px', height: '20px' }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      <>
                        <FaGoogle /> Login with Google
                      </>
                    )}
                  </span>
                </Button>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
      <WorkspaceCards />
    </div>
  );
}